import React, { useState } from 'react';
import { Heart, Leaf, Truck, ShieldCheck, Star, Award, CheckCircle2, Utensils, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

const About = () => {
  const [activeValue, setActiveValue] = useState(null);

  const values = [
    {
      icon: <Heart className="w-12 h-12 text-red-500" />,
      title: 'Passion for Food',
      description: 'We pour our heart into every dish, ensuring each meal is a delightful experience',
      color: 'from-red-900/20 to-red-600/10',
      border: 'border-red-500/30'
    },
    {
      icon: <Leaf className="w-12 h-12 text-green-500" />,
      title: 'Fresh Ingredients',
      description: 'Only the freshest, highest quality ingredients make it to our kitchen',
      color: 'from-green-900/20 to-green-600/10',
      border: 'border-green-500/30'
    },
    {
      icon: <ShieldCheck className="w-12 h-12 text-blue-500" />,
      title: 'Food Safety',
      description: '100% hygienic preparation following strict quality and safety standards',
      color: 'from-blue-900/20 to-blue-600/10',
      border: 'border-blue-500/30'
    },
    {
      icon: <Truck className="w-12 h-12 text-gold-500" />,
      title: 'Timely Delivery',
      description: 'Your food arrives hot, fresh, and on time, every single time',
      color: 'from-gold-900/20 to-gold-600/10',
      border: 'border-gold-500/30'
    }
  ];

  return (
    <div className="min-h-screen bg-background pb-20 relative overflow-hidden">
      {/* Background Gradients */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-gold-500/10 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-purple-900/10 rounded-full blur-3xl opacity-30" />
      </div>

      <div className="container mx-auto px-6 pt-40 pb-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center justify-center p-3 bg-gold-500/10 rounded-full mb-6 relative group">
            <div className="absolute inset-0 bg-gold-500/20 rounded-full blur-xl group-hover:bg-gold-500/40 transition-colors" />
            <Sparkles className="w-8 h-8 text-gold-500 relative z-10 animate-pulse" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-300 via-gold-500 to-gold-300 mb-6 font-serif">
            About Magic World
          </h1>
          <p className="max-w-3xl mx-auto text-xl text-gray-400 leading-relaxed">
            Where Authentic Flavors Meet Modern Convenience.
            <br /> We craft culinary experiences that bring people together.
          </p>
        </motion.div>

        {/* Our Story */}
        <section className="mb-24 relative">
          <div className="absolute -left-20 top-0 w-64 h-64 bg-gold-500/5 rounded-full blur-3xl" />
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gray-900/40 border border-white/5 rounded-3xl p-8 md:p-12 backdrop-blur-sm relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-gold-500/5 to-transparent rounded-full blur-3xl group-hover:bg-gold-500/10 transition-colors duration-1000" />
            <div className="relative z-10 flex flex-col md:flex-row gap-12 items-center">
              <div className="flex-1 space-y-6">
                <h2 className="text-3xl md:text-5xl font-bold text-gold-500 font-serif mb-2">Our Story</h2>
                <div className="w-20 h-1 bg-gradient-to-r from-gold-500 to-transparent rounded-full" />

                <div className="space-y-6 text-lg leading-relaxed text-gray-300 font-light">
                  <p>
                    Magic World was born from a simple yet powerful vision: to bring the magic of
                    authentic, homestyle cooking to every home in Pakistan. We understand that in
                    today's fast-paced world, everyone deserves access to delicious, wholesome meals
                    without compromising on quality or taste.
                  </p>
                  <p>
                    What started as a small kitchen with big dreams has grown into a beloved cloud
                    kitchen serving hundreds of happy customers daily. Our secret? We treat every dish
                    as if we're cooking for our own family – with love, care, and attention to detail.
                  </p>
                  <p>
                    At Magic World, we believe that great food has the power to create magical moments.
                    Whether it's a family dinner, a celebration, or a simple craving for comfort food,
                    we're here to make every meal special.
                  </p>
                </div>
              </div>
              <div className="flex-1 relative">
                <div className="aspect-square rounded-2xl overflow-hidden border-2 border-gold-500/20 shadow-2xl shadow-gold-900/20 rotate-3 hover:rotate-0 transition-transform duration-500">
                  <img src="/about-story.jpg" alt="Our Kitchen" className="w-full h-full object-cover" onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1556910103-1c02745a30bf?auto=format&fit=crop&q=80&w=1000' }} />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-gray-900 p-6 rounded-2xl border border-gold-500 shadow-xl max-w-xs transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                  <Quote className="w-8 h-8 text-gold-500 mb-2" />
                  <p className="text-gray-300 text-sm italic">"Food is not just fuel, it's an emotion, a memory, and a language of love."</p>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Mission & Vision */}
        <section className="mb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-10 rounded-3xl bg-gradient-to-br from-gray-900 to-gray-800 border border-gold-500/20 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                <Star className="w-32 h-32 text-gold-500" />
              </div>
              <h2 className="flex items-center mb-6 font-serif text-3xl font-bold text-gold-500 relative z-10">
                <Star className="mr-3 w-8 h-8" /> Our Mission
              </h2>
              <p className="text-lg leading-relaxed text-gray-300 relative z-10">
                To deliver not just food, but happiness – one meal at a time. We're committed to
                providing our customers with restaurant-quality dishes made in home from fresh ingredients,
                prepared hygienically , and delivered with a smile.
              </p>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-10 rounded-3xl bg-gradient-to-br from-gray-900 to-gray-800 border border-gold-500/20 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                <Award className="w-32 h-32 text-gold-500" />
              </div>
              <h2 className="flex items-center mb-6 font-serif text-3xl font-bold text-gold-500 relative z-10">
                <Award className="mr-3 w-8 h-8" /> Our Vision
              </h2>
              <p className="text-lg leading-relaxed text-gray-300 relative z-10">
                To become Pakistan's most trusted and loved cloud kitchen, known for exceptional
                food quality, customer service, and innovation in delivering authentic flavors to
                every doorstep across the Rawalpindi/Islamabad.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Our Values */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <span className="text-gold-500 text-sm font-bold uppercase tracking-widest mb-2 block">Core Principles</span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">What We Stand For</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onMouseEnter={() => setActiveValue(index)}
                onMouseLeave={() => setActiveValue(null)}
                className={`p-8 rounded-2xl bg-gradient-to-br border transition-all duration-500 h-full relative overflow-hidden group ${activeValue === index ? value.color + ' ' + value.border : 'from-gray-900 to-gray-800 border-white/5'
                  }`}
              >
                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">
                  {value.icon}
                </div>
                <h3 className="mb-3 text-xl font-bold text-white group-hover:text-gold-400 transition-colors">{value.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed group-hover:text-gray-200 transition-colors">{value.description}</p>

                {/* Decorative element */}
                <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-white/5 rounded-full blur-xl group-hover:bg-white/10 transition-colors" />
              </motion.div>
            ))}
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="mb-24 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-gold-900/10 to-transparent rounded-3xl" />
          <div className="p-8 md:p-12 border border-white/10 rounded-3xl bg-gray-900/40 backdrop-blur-md">
            <h2 className="mb-12 font-serif text-3xl font-bold text-center md:text-5xl text-gold-500">
              Why Choose Magic World?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-8 gap-x-12">
              {[
                { title: 'Authentic Recipes', desc: 'Traditional recipes passed down through generations' },
                { title: 'Expert Chefs', desc: 'Trained culinary professionals with years of experience' },
                { title: 'Quality Ingredients', desc: 'Sourced from trusted suppliers, always fresh' },
                { title: 'Hygienic Kitchen', desc: 'Maintained to the highest cleanliness standards' },
                { title: 'Fast Delivery', desc: 'Hot food delivered within 30-45 minutes' },
                { title: 'Customer First', desc: 'Your satisfaction is our top priority' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start space-x-4 group">
                  <div className="mt-1 w-12 h-12 rounded-full bg-gold-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-gold-500 group-hover:text-black transition-all duration-300">
                    <CheckCircle2 className="w-6 h-6 text-gold-500 group-hover:text-black transition-colors" />
                  </div>
                  <div>
                    <h4 className="mb-2 text-lg font-bold text-white group-hover:text-gold-400 transition-colors">{item.title}</h4>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Commitment */}
        <section className="text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="p-12 rounded-3xl bg-gradient-to-b from-gray-900 via-black to-gold-900/20 border border-gold-500/30 shadow-2xl shadow-gold-900/20 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold-500 to-transparent opacity-50" />

            <h2 className="mb-6 font-serif text-3xl font-bold md:text-4xl text-gold-500">
              Our Commitment to You
            </h2>
            <div className="mb-8 text-gold-500/20 flex justify-center">
              <Utensils className="w-16 h-16" />
            </div>
            <p className="text-xl leading-relaxed text-gray-300 font-light mb-8">
              "We promise to always deliver food that we'd be proud to serve to our own families.
              Every dish is prepared with care, packed securely, and delivered promptly. Your trust
              in Magic World means everything to us, and we work hard every day to earn and keep it."
            </p>
            <div className="flex items-center justify-center gap-2 text-gold-400 font-serif italic text-lg">
              <Sparkles className="w-5 h-5" />
              <span>Creating magical moments, one meal at a time</span>
              <Sparkles className="w-5 h-5" />
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  );
};

const Quote = ({ className }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H15.017C14.4647 8 14.017 8.44772 14.017 9V11C14.017 11.5523 13.5693 12 13.017 12H12.017V5H22.017V15C22.017 18.3137 19.3307 21 16.017 21H14.017ZM5.0166 21L5.0166 18C5.0166 16.8954 5.91203 16 7.0166 16H10.0166C10.5689 16 11.0166 15.5523 11.0166 15V9C11.0166 8.44772 10.5689 8 10.0166 8H6.0166C5.46432 8 5.0166 8.44772 5.0166 9V11C5.0166 11.5523 4.56889 12 4.0166 12H3.0166V5H13.0166V15C13.0166 18.3137 10.3303 21 7.0166 21H5.0166Z" />
  </svg>
);

export default About;

